import { useEffect, useEffectEvent, useRef } from "react";

import { useAppToast } from "@/src/hooks/useAppToast";

/**
 * Signale l'échec d'une **query** par un toast d'erreur.
 *
 * React Query v5 a retiré `onError` des queries : un effet reste la seule
 * option. Deux précautions, reprises de wargame-app :
 *
 * - **Un toast par épisode d'erreur**, pas un par render — d'où le drapeau,
 *   qui retombe dès que `isError` repasse à `false` (un échec de refetch après
 *   reprise réaffiche donc bien un toast) ;
 * - **pas de `exhaustive-deps` désactivé** : l'affichage passe par un
 *   `useEffectEvent`, qui voit toujours les derniers libellés sans figurer dans
 *   les dépendances.
 *
 * @param enabled - `false` pour les écrans qui rendent l'erreur eux-mêmes.
 */
export function useQueryErrorToast(
  isError: boolean,
  label: string,
  error?: unknown,
  enabled = true,
) {
  const { showApiError } = useAppToast();

  const hasShownRef = useRef(false);

  const showOnce = useEffectEvent(() => {
    hasShownRef.current = true;
    showApiError(label, error);
  });

  useEffect(() => {
    if (!isError) {
      hasShownRef.current = false;
      return;
    }
    if (!enabled || hasShownRef.current) return;

    showOnce();
  }, [isError, enabled]);
}
